import { ReactNode } from "react";
import { UIBox, UIButton, UIDialog, UIDialogActions, UIDialogContent, UIDialogTitle } from "./index";
import { createStylesheet } from "../utils/styles";

interface Props {
  title?: string;
  children?: ReactNode;
  open: boolean;
  setOpen: Function;
  onConfirm: Function | void | undefined;
  onCancel?: Function | void | undefined;
  disableOnSubmit?: boolean;
  onSubmitTitle?: string;
  onCancelTitle?: string;
  isDelete?: boolean;
  icon?: ReactNode;
  isProcessing?: boolean;
  width?: "xs" | "sm" | "md" | "lg" | "xl";
}

const UIConfirm = ({
  title,
  children,
  open,
  setOpen,
  onConfirm,
  onCancel,
  disableOnSubmit,
  onSubmitTitle,
  onCancelTitle,
  isDelete = false,
  icon,
  isProcessing,
  width = "xs",
}: Props) => {
  const classes = useStyles();

  const handleCancel = () => {
    if (isProcessing) return;
    if (onCancel) onCancel();
    setOpen(false);
  };

  const handleConfirm = async () => {
    if (isProcessing) return;
    const result = onConfirm ? onConfirm() : undefined;
    if (result && typeof (result as Promise<unknown>).then === "function") {
      // Async confirm: keep the dialog open (showing isProcessing) until the action settles.
      try {
        await result;
      } catch {
        // Error feedback is the caller's responsibility (mutation onError handlers).
      } finally {
        setOpen(false);
      }
      return;
    }
    // Sync confirm with an externally managed isProcessing flag: the parent closes the dialog.
    if (isProcessing === undefined) setOpen(false);
  };

  return (
    <UIDialog open={open} onClose={handleCancel} data-cy="confirm-dialog" fullWidth maxWidth={width}>
      <div className={classes.dialog}>
        {Boolean(title) && (
          <UIDialogTitle id="confirm-dialog">
            {icon ? (
              <UIBox className={classes.titleWithIcon}>
                {icon}
                {title}
              </UIBox>
            ) : (
              title
            )}
          </UIDialogTitle>
        )}
        <UIDialogContent>{children}</UIDialogContent>
        <UIDialogActions className={classes.buttons}>
          <UIButton variant="outlined" onClick={handleCancel} disabled={isProcessing} className={classes.cancelButton}>
            {onCancelTitle || "Cancel"}
          </UIButton>
          <UIButton
            data-cy="confirm-dialog-submit"
            variant="contained"
            color={isDelete ? "error" : "primary"}
            disabled={disableOnSubmit}
            isLoading={isProcessing}
            onClick={handleConfirm}
          >
            {onSubmitTitle || "Yes"}
          </UIButton>
        </UIDialogActions>
      </div>
    </UIDialog>
  );
};

const useStyles = createStylesheet((theme) => ({
  titleWithIcon: {
    display: "flex",
    alignItems: "center",
    gap: 1,
  },
  dialog: {
    margin: theme.spacing(2),
    whiteSpace: "pre-wrap",
  },
  buttons: {
    marginTop: theme.spacing(2),
  },
  cancelButton: {
    marginRight: theme.spacing(1),
  },
}));

export default UIConfirm;
